import { useSettings } from '../Settings/SettingsProvider';
import {
  shapeToVectorLayer,
  useLayers,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import React, { useCallback, useMemo } from 'react';
import { isShapeLabelShown } from './isShapeShownOnStartUp';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { PiEye, PiEyeSlash } from 'react-icons/pi';
import { GeoJson } from '@ais-ui/api-spec/shapes';

interface ShowHideShapeButtonProps {
  shape: GeoJson;
}

export const ShowHideShapeButton = ({ shape }: ShowHideShapeButtonProps) => {
  const { settings, dataUpdatedAt } = useSettings();
  const { mapProjection, userProjection } = useOpenLayersContext();
  const { addLayer, doesLayerExist, zoomToLayer, removeLayerById } =
    useLayers();

  const isShapeShown = useMemo(() => {
    return doesLayerExist(shape.properties.id as string);
  }, [doesLayerExist, shape]);

  const isLabelShown = useMemo(() => {
    return isShapeLabelShown(settings, shape.properties.id as string);
  }, [settings, shape.properties.id as string, dataUpdatedAt]);

  const onShow = useCallback(() => {
    const layer = shapeToVectorLayer(shape);
    const name = shape?.properties?.name || 'No name';

    addLayer(
      layer,
      name,
      true,
      true,
      true,
      shape.properties,
      shape.properties.id,
      isLabelShown
    );
    zoomToLayer(layer);
  }, [
    shape,
    userProjection,
    mapProjection,
    addLayer,
    zoomToLayer,
    isLabelShown,
  ]);

  const onRemove = useCallback(() => {
    removeLayerById(shape.properties.id as string);
  }, [shape, removeLayerById]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          id={`is-shape-shown-button-${shape.properties.name}`}
          size="smIcon"
          variant="ghost"
          onClick={isShapeShown ? onRemove : onShow}
        >
          {isShapeShown ? (
            <PiEyeSlash className="h-4 w-4" />
          ) : (
            <PiEye className="h-4 w-4"></PiEye>
          )}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {isShapeShown ? 'Remove shape from map' : 'Add shape to map'}
      </TooltipContent>
    </Tooltip>
  );
};
